import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'

interface Section {
  id: string
  title: string
}

export function PrivacyContent() {
  const { t } = useTranslation('common')
  const [activeSection, setActiveSection] = useState<string>('')

  const sections: Section[] = [
    { id: 'data-controller', title: t('privacy.sections.dataController.title') },
    { id: 'data-collected', title: t('privacy.sections.dataCollected.title') },
    { id: 'purpose', title: t('privacy.sections.purpose.title') },
    { id: 'legal-basis', title: t('privacy.sections.legalBasis.title') },
    { id: 'retention', title: t('privacy.sections.retention.title') },
    { id: 'user-rights', title: t('privacy.sections.userRights.title') },
    { id: 'cookies', title: t('privacy.sections.cookies.title') },
    { id: 'contact', title: t('privacy.sections.contact.title') },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map((s) => ({
        id: s.id,
        element: document.getElementById(s.id),
      }))

      const scrollPosition = window.scrollY + 200

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, element } = sectionElements[i]
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const headerOffset = 120
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <section className="bg-white py-16 md:py-24" aria-labelledby="privacy-content-title">
      <div className="container-fiorucci mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Table of Contents - Sticky Sidebar */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-32">
                <h2
                  id="privacy-content-title"
                  className="text-lg font-bold text-[#4E2118] mb-4 font-sans"
                >
                  {t('privacy.toc.title')}
                </h2>
                <nav aria-label={t('privacy.toc.ariaLabel')}>
                  <ul className="space-y-2">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`text-left w-full text-sm transition-all duration-200 px-3 py-2 rounded-lg ${
                            activeSection === section.id
                              ? 'bg-[#DF631E]/10 text-[#DF631E] font-semibold'
                              : 'text-[#4E2118]/70 hover:text-[#4E2118] hover:bg-[#4E2118]/5'
                          }`}
                          aria-current={activeSection === section.id ? 'true' : undefined}
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Last Updated */}
                <div className="mt-8 pt-6 border-t border-[#4E2118]/10">
                  <p className="text-xs text-[#4E2118]/50">
                    {t('privacy.lastUpdated')}: {t('privacy.lastUpdatedDate')}
                  </p>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-12">
              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <p className="text-[#4E2118]/80 leading-relaxed text-lg">
                  {t('privacy.introduction')}
                </p>
              </div>

              {/* Section 1: Data Controller */}
              <article id="data-controller" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.dataController.title')}
                </h2>
                <div className="bg-[#FDF8F3] rounded-2xl p-6 md:p-8">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-[#4E2118]/60 uppercase tracking-wider mb-1">
                        {t('privacy.sections.dataController.companyLabel')}
                      </h3>
                      <p className="text-[#4E2118] font-medium text-lg">
                        {t('privacy.sections.dataController.company')}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#4E2118]/60 uppercase tracking-wider mb-1">
                        {t('privacy.sections.dataController.addressLabel')}
                      </h3>
                      <p className="text-[#4E2118]">
                        {t('privacy.sections.dataController.address')}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#4E2118]/60 uppercase tracking-wider mb-1">
                        {t('privacy.sections.dataController.emailLabel')}
                      </h3>
                      <a
                        href={`mailto:${t('privacy.sections.dataController.email')}`}
                        className="text-[#DF631E] hover:underline"
                      >
                        {t('privacy.sections.dataController.email')}
                      </a>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#4E2118]/60 uppercase tracking-wider mb-1">
                        {t('privacy.sections.dataController.vatLabel')}
                      </h3>
                      <p className="text-[#4E2118]">{t('privacy.sections.dataController.vat')}</p>
                    </div>
                  </div>
                </div>
              </article>

              {/* Section 2: Types of Data Collected */}
              <article id="data-collected" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.dataCollected.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.dataCollected.intro')}</p>

                  <div className="bg-white border border-[#4E2118]/10 rounded-xl p-6">
                    <h3 className="font-semibold text-[#4E2118] mb-4">
                      {t('privacy.sections.dataCollected.personalData.title')}
                    </h3>
                    <ul className="space-y-2 list-disc list-inside">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <li key={i}>{t(`privacy.sections.dataCollected.personalData.items.${i}`)}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-white border border-[#4E2118]/10 rounded-xl p-6">
                    <h3 className="font-semibold text-[#4E2118] mb-4">
                      {t('privacy.sections.dataCollected.usageData.title')}
                    </h3>
                    <ul className="space-y-2 list-disc list-inside">
                      {[0, 1, 2, 3].map((i) => (
                        <li key={i}>{t(`privacy.sections.dataCollected.usageData.items.${i}`)}</li>
                      ))}
                    </ul>
                  </div>

                  <p>{t('privacy.sections.dataCollected.optional')}</p>
                </div>
              </article>

              {/* Section 3: Purpose of Processing */}
              <article id="purpose" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.purpose.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.purpose.intro')}</p>

                  <div className="grid gap-4">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="flex gap-4 p-4 bg-[#FDF8F3] rounded-xl"
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#DF631E] text-white flex items-center justify-center font-bold text-sm">
                          {i + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#4E2118] mb-1">
                            {t(`privacy.sections.purpose.items.${i}.title`)}
                          </h4>
                          <p className="text-sm text-[#4E2118]/70">
                            {t(`privacy.sections.purpose.items.${i}.description`)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              {/* Section 4: Legal Basis */}
              <article id="legal-basis" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.legalBasis.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.legalBasis.intro')}</p>

                  <div className="space-y-4">
                    {['consent', 'contract', 'legal', 'legitimate'].map((key) => (
                      <div
                        key={key}
                        className="border-l-4 border-[#DF631E] pl-6 py-2"
                      >
                        <h4 className="font-semibold text-[#4E2118] mb-2">
                          {t(`privacy.sections.legalBasis.basis.${key}.title`)}
                        </h4>
                        <p className="text-[#4E2118]/70">
                          {t(`privacy.sections.legalBasis.basis.${key}.description`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              {/* Section 5: Data Retention */}
              <article id="retention" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.retention.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.retention.intro')}</p>

                  <div className="bg-[#FDF8F3] rounded-xl p-6 space-y-4">
                    {['contact', 'account', 'marketing', 'legal'].map((key) => (
                      <div key={key} className="flex justify-between items-start gap-4 pb-4 border-b border-[#4E2118]/10 last:border-0 last:pb-0">
                        <div>
                          <h4 className="font-semibold text-[#4E2118]">
                            {t(`privacy.sections.retention.periods.${key}.label`)}
                          </h4>
                          <p className="text-sm text-[#4E2118]/60">
                            {t(`privacy.sections.retention.periods.${key}.context`)}
                          </p>
                        </div>
                        <span className="text-sm font-semibold text-[#DF631E] whitespace-nowrap">
                          {t(`privacy.sections.retention.periods.${key}.duration`)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-[#4E2118]/60 italic">
                    {t('privacy.sections.retention.note')}
                  </p>
                </div>
              </article>

              {/* Section 6: User Rights */}
              <article id="user-rights" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.userRights.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.userRights.intro')}</p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {['access', 'rectification', 'erasure', 'restriction', 'portability', 'objection'].map((key) => (
                      <div
                        key={key}
                        className="p-4 bg-white border border-[#4E2118]/10 rounded-xl hover:border-[#DF631E]/30 transition-colors"
                      >
                        <h4 className="font-semibold text-[#4E2118] mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#DF631E]" />
                          {t(`privacy.sections.userRights.rights.${key}.title`)}
                        </h4>
                        <p className="text-sm text-[#4E2118]/70">
                          {t(`privacy.sections.userRights.rights.${key}.description`)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#DF631E]/5 border border-[#DF631E]/20 rounded-xl p-6">
                    <h4 className="font-semibold text-[#4E2118] mb-2">
                      {t('privacy.sections.userRights.exercise.title')}
                    </h4>
                    <p className="text-[#4E2118]/80">
                      {t('privacy.sections.userRights.exercise.description')}
                    </p>
                  </div>
                </div>
              </article>

              {/* Section 7: Cookies */}
              <article id="cookies" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.cookies.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.cookies.intro')}</p>

                  <div className="space-y-4">
                    <div className="bg-white border border-[#4E2118]/10 rounded-xl p-6">
                      <h4 className="font-semibold text-[#4E2118] mb-3">
                        {t('privacy.sections.cookies.types.necessary.title')}
                      </h4>
                      <p className="text-sm text-[#4E2118]/70 mb-3">
                        {t('privacy.sections.cookies.types.necessary.description')}
                      </p>
                      <span className="inline-block px-3 py-1 text-xs font-medium text-white bg-[#4E2118] rounded-full">
                        {t('privacy.sections.cookies.alwaysActive')}
                      </span>
                    </div>

                    <div className="bg-white border border-[#4E2118]/10 rounded-xl p-6">
                      <h4 className="font-semibold text-[#4E2118] mb-3">
                        {t('privacy.sections.cookies.types.analytics.title')}
                      </h4>
                      <p className="text-sm text-[#4E2118]/70">
                        {t('privacy.sections.cookies.types.analytics.description')}
                      </p>
                    </div>

                    <div className="bg-white border border-[#4E2118]/10 rounded-xl p-6">
                      <h4 className="font-semibold text-[#4E2118] mb-3">
                        {t('privacy.sections.cookies.types.marketing.title')}
                      </h4>
                      <p className="text-sm text-[#4E2118]/70">
                        {t('privacy.sections.cookies.types.marketing.description')}
                      </p>
                    </div>
                  </div>

                  <p>{t('privacy.sections.cookies.management')}</p>
                </div>
              </article>

              {/* Section 8: Contact */}
              <article id="contact" className="scroll-mt-32">
                <h2 className="text-2xl md:text-3xl font-bold text-[#4E2118] mb-6 font-serif">
                  {t('privacy.sections.contact.title')}
                </h2>
                <div className="space-y-6 text-[#4E2118]/80 leading-relaxed">
                  <p>{t('privacy.sections.contact.intro')}</p>

                  <div className="bg-[#5C1A1A] text-white rounded-2xl p-8">
                    <h3 className="text-xl font-semibold mb-6">
                      {t('privacy.sections.contact.dpo.title')}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm text-white/60 uppercase tracking-wider mb-1">
                          {t('privacy.sections.contact.dpo.emailLabel')}
                        </h4>
                        <a
                          href={`mailto:${t('privacy.sections.contact.dpo.email')}`}
                          className="text-[#DF631E] hover:underline text-lg"
                        >
                          {t('privacy.sections.contact.dpo.email')}
                        </a>
                      </div>
                      <div>
                        <h4 className="text-sm text-white/60 uppercase tracking-wider mb-1">
                          {t('privacy.sections.contact.dpo.addressLabel')}
                        </h4>
                        <p className="text-white/90">
                          {t('privacy.sections.contact.dpo.address')}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm text-white/60 uppercase tracking-wider mb-1">
                          {t('privacy.sections.contact.dpo.phoneLabel')}
                        </h4>
                        <a
                          href={`tel:${t('privacy.sections.contact.dpo.phone').replace(/\s/g, '')}`}
                          className="text-white/90 hover:text-white"
                        >
                          {t('privacy.sections.contact.dpo.phone')}
                        </a>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-[#4E2118]/60">
                    {t('privacy.sections.contact.authority')}
                  </p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
